import { ComboboxOption } from '@headlessui/react';
import { CheckIcon, ArrowTopRightOnSquareIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useField } from 'formik';
import React, { useEffect, useState } from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import { pageKey } from '@dashboard/lib/queryKeys';
import useSearchInput from '@/dashboard/hooks/useSearchInput';
import Combobox from '../../Form/components/Combobox';

import Lozenge from '@dashboard/components/Lozenge';
import Button from '@/dashboard/components/Button';
import { formatEventDate } from '@/dashboard/lib/format-date';

const PageSelect = ({ showLabel = false, name, pageTypes = ['checkout', 'event', 'form'] }) => {
  // eslint-disable-next-line no-unused-vars
  const [formikField, formikMeta, formikHelpers] = useField(name);

  const { getPages, getPage } = usePageQueries();
  const { sellerId } = useAuth();
  const [selectedPage, setSelectedPage] = useState();
  const { setSearchInput, search } = useSearchInput('');

  const { data: currentPageRedirect } = useQuery({
    queryKey: pageKey(formikField.value),
    queryFn: () => getPage(formikField.value),
    enabled: !!formikField.value,
  });

  useEffect(() => {
    if (!selectedPage) {
      setSelectedPage(currentPageRedirect);
      setSearchInput(currentPageRedirect?.name);
    }
  }, [currentPageRedirect]);

  useEffect(() => {
    if (!selectedPage) return;

    if (selectedPage._id !== formikField.value) {
      formikHelpers.setValue(selectedPage._id);
    }
  }, [selectedPage]);

  const { data, fetchNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['pages', search],
    queryFn: async ({ pageParam }) =>
      await getPages(sellerId, {
        status: ['published', 'draft'],
        type: pageTypes,
        limit: 10,
        skip: pageParam * 10,
        search: search === '' ? undefined : search,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
  });

  const openLink = pageId => {
    window.open(`/dashboard/pages/${pageId}/customize/product`, '_blank');
  };

  const clear = () => {
    formikHelpers.setValue(null);
    setSearchInput('');
    setSelectedPage(null);
  };

  return (
    <>
      <Combobox
        name={name}
        immediate
        value={selectedPage}
        onChange={value => {
          if (value == null) {
            clear();
            return;
          }

          setSearchInput(value.name);
          setSelectedPage(value);
        }}
        onClear={e => {
          e.preventDefault();
          clear();
        }}
        onChangeInput={e => setSearchInput(e.target.value)}
        placeholder="Search for a page"
        hasItems={data?.pages?.[0]?.data?.length > 0}
        isError={isError}
        isLoading={isLoading}
        noItemsMsg="No pages found"
        fetchNextPage={fetchNextPage}
        label="Next page"
        tooltip="Display a new page after checkout or submission"
        showLabel={showLabel}
      >
        {data?.pages?.map(data =>
          data?.data.map(page => (
            <ComboboxOption
              key={page._id}
              value={page}
              className={({ focus }) => `cursor-pointer relative ${focus ? 'bg-gray-100' : ''}`}
            >
              <div
                key={page._id}
                type="button"
                className={clsx(
                  'flex w-full p-2 hover:bg-gray-100 transition-colors border-b border-gray-100',
                )}
              >
                <div>
                  <div className="text-xs text-gray-400 capitalize">{page.type}</div>
                  <div className="text-sm font-semibold truncate">{page.name}</div>
                  {page.type === 'checkout' && (
                    <div className="text-gray-700">
                      <span>{getProductPriceLabel(page.product)}</span>
                      {page.product?.productId != null && page.product?.planId != null && (
                        <div className="space-x-1">
                          <Lozenge>{page.product.productId}</Lozenge>
                          <Lozenge>{page.product.planId}</Lozenge>
                        </div>
                      )}
                    </div>
                  )}
                  {page.type === 'event' && page.eventDetails.startDate && (
                    <div className="text-gray-700">
                      <div className="text-gray-700 text-sm">
                        <div className="flex items-center gap-x-1 justify-start">
                          <CalendarIcon className="h-4 w-4" />

                          <div className="flex flex-col font-medium">
                            {formatEventDate({
                              startDate: page.eventDetails.startDate,
                              endDate: page.eventDetails.endDate,
                              timezone: page.eventDetails.timezone,
                              locale: page.locale,
                            }).map(date => (
                              <span key={date}>{date}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex ml-auto">
                  {page._id === selectedPage?._id && (
                    <div className="flex gap-x-4 ml-auto items-center">
                      <CheckIcon className="w-5 h-5 text-gray-500" />
                    </div>
                  )}

                  <div className="flex items-center">
                    <Button
                      appearance="icon-button"
                      onClick={() => openLink(page._id)}
                      type="button"
                      onMouseDown={e => e.preventDefault()}
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-500" />
                    </Button>
                  </div>
                </div>
              </div>
            </ComboboxOption>
          )),
        )}
      </Combobox>
    </>
  );
};

export default PageSelect;
